/* eslint-disable security/detect-object-injection */
import { Host, h } from "@stencil/core";
let id = 0;
export class CoreTabsVertical {
    constructor() {
        this.tabsId = `tabs-vertical-${id++}`;
        this.activeTabIndex = 0;
        this.width = 'small';
        this.internalActiveTabIndex = 0;
        this.internalFocusedTabIndex = 0;
        this.tabRefs = [];
        this.tabHeaderRefs = [];
        this.tabObjects = [];
    }
    setActiveAndFocusedTab(index) {
        this.internalActiveTabIndex = index;
        this.internalFocusedTabIndex = index;
    }
    activeTabIndexChanged(newValue, oldValue) {
        if (newValue !== oldValue) {
            this.setActiveAndFocusedTab(newValue);
        }
    }
    componentWillLoad() {
        if (this.activeTabIndex)
            this.internalActiveTabIndex = this.activeTabIndex;
        if (this.internalActiveTabIndex !== this.activeTabIndex) {
            this.coreTabChange.emit({ previousTab: this.internalActiveTabIndex, currentTab: this.activeTabIndex });
        }
        this.internalFocusedTabIndex = this.internalActiveTabIndex;
        this.onSlotChange();
    }
    // when a slot is removed / added
    onSlotChange() {
        const tabs = this.element.querySelectorAll('core-tab-panel');
        this.tabObjects = Array.from(tabs).map(tab => {
            const pillNumber = tab.getAttribute('notification-pill-number') !== '' ? tab.getAttribute('notification-pill-number') : 'undefined';
            return {
                label: tab.getAttribute('label'),
                iconName: tab.getAttribute('icon-name'),
                notificationPillNumber: pillNumber,
                disabled: tab.getAttribute('disabled') === 'true'
            };
        });
        // Ensure icons have the right variant
        this.setIconDependingOnSelectedTab();
        this.tabRefs = Array.from(tabs);
        this.tabRefs.forEach((tab, index) => {
            tab.setAttribute('slot', `${this.tabsId}_tab-${index}`);
        });
    }
    setIconDependingOnSelectedTab() {
        this.tabObjects.forEach((tabPanel, index) => {
            if (!tabPanel.iconName)
                return;
            if (index !== this.internalActiveTabIndex || this.tabObjects[index].disabled) {
                this.tabObjects[index].iconName = tabPanel.iconName.replace('-solid', '-outline');
            }
            else {
                this.tabObjects[index].iconName = tabPanel.iconName.replace('-outline', '-solid');
            }
        });
    }
    componentDidLoad() {
        // Add keyboard event listeners for each tab header
        this.tabHeaderRefs.forEach((tab, index) => {
            tab.addEventListener('focus', this.onTabFocus(index));
        });
        document.addEventListener('click', event => {
            if (!event.composedPath().includes(this.element)) {
                this.updateTabFocusability();
            }
        });
    }
    onTabFocus(index) {
        return () => {
            this.internalFocusedTabIndex = index;
        };
    }
    disconnectedCallback() {
        // Remove keyboard event listeners when component is unmounted
        this.tabHeaderRefs.forEach((tab, index) => {
            tab.removeEventListener('focus', this.onTabFocus(index));
        });
    }
    componentDidUpdate() {
        this.updateTabFocusability();
    }
    updateTabFocusability() {
        this.tabHeaderRefs.forEach((tab, index) => {
            tab.tabIndex = index === this.internalActiveTabIndex ? 0 : -1;
        });
    }
    focusNextTab() {
        let nextIndex = this.internalFocusedTabIndex + 1;
        while (nextIndex < this.tabHeaderRefs.length && this.tabObjects[nextIndex].disabled) {
            nextIndex++;
        }
        if (nextIndex >= 0 && nextIndex < this.tabHeaderRefs.length) {
            this.internalFocusedTabIndex = nextIndex;
            this.tabHeaderRefs[nextIndex].focus();
        }
    }
    focusPreviousTab() {
        let prevIndex = this.internalFocusedTabIndex - 1;
        while (prevIndex >= 0 && this.tabObjects[prevIndex].disabled) {
            prevIndex--;
        }
        if (prevIndex >= 0 && prevIndex < this.tabHeaderRefs.length) {
            this.internalFocusedTabIndex = prevIndex;
            this.tabHeaderRefs[prevIndex].focus();
        }
    }
    getTabItemClass(index) {
        const isActive = index === this.internalActiveTabIndex && !this.tabObjects[index].disabled;
        const isDisabled = this.tabObjects[index].disabled;
        return `core-tab-item ${isActive ? 'tab-item--selected' : ''} ${isDisabled ? 'tab-item--disabled' : ''}`;
    }
    handleClick(tab, index) {
        if (tab.disabled)
            return;
        this.coreTabChange.emit({ previousTab: this.internalActiveTabIndex, currentTab: index });
        this.internalActiveTabIndex = index;
        // Ensure to get back to original icon
        this.setIconDependingOnSelectedTab();
    }
    handleKeyDown(event) {
        if (event.key === 'Tab') {
            if (event.shiftKey) {
                // Shift + Tab
                if (this.internalFocusedTabIndex === 0) {
                    // Allow default behavior to move focus out of component
                }
                else {
                    event.preventDefault();
                    this.focusPreviousTab();
                }
            }
            else {
                // Tab
                if (this.internalFocusedTabIndex === this.tabHeaderRefs.length - 1) {
                    // Allow default behavior to move focus out of component
                }
                else {
                    event.preventDefault();
                    this.focusNextTab();
                }
            }
        }
        else if (event.key === 'Enter' && this.internalFocusedTabIndex !== -1 && !this.tabObjects[this.internalFocusedTabIndex].disabled) {
            const previouslyActiveTabIndex = this.internalActiveTabIndex;
            this.internalActiveTabIndex = this.internalFocusedTabIndex;
            this.coreTabChange.emit({ previousTab: previouslyActiveTabIndex, currentTab: this.internalFocusedTabIndex });
        }
    }
    render() {
        return (h(Host, { key: '6a8d7997afa580f425c2490f4a897eb2dadbbb86' }, h("div", { key: '831d2fc73d9667a407eb03c9046e10355960ccb3', id: this.tabsId, class: "core-tabs core-tabs--vertical" }, h("ul", { key: '2cf136b96819b2971071d8edaba27d0073dac3a1', role: "tablist", class: `core-tabs-list core-tabs-list--${this.width} typography-monoline-medium` }, this.tabObjects.map((tab, index) => (h("li", { id: `tab-label-${index}`, class: this.getTabItemClass(index), ref: el => (this.tabHeaderRefs[index] = el), tabindex: "0", onClick: () => {
                this.handleClick(tab, index);
            }, "aria-selected": index === this.internalActiveTabIndex ? 'true' : 'false', "aria-disabled": tab.disabled ? 'true' : 'false', role: "tab", "aria-controls": `${this.tabsId}_tab-panel-${index}` }, h("div", { class: "tab-label-content" }, tab.iconName && h("core-icon", { name: tab.iconName, size: "number-size-icon-medium" }), h("span", { class: "tab-label" }, tab.label), tab.notificationPillNumber && typeof tab.notificationPillNumber !== 'undefined' && (h("core-notification-pill", { number: tab.notificationPillNumber }))))))), h("div", { key: 'b6b556929fbcf3ced049220e853fd89fa83b8b93', class: "tab-content" }, Array.from(this.tabObjects).map((_, index) => (h("div", { id: `${this.tabsId}_tab-panel-${index}`, role: "tabpanel", "aria-labelledby": `tab-label-${index}`, style: { display: index === this.internalActiveTabIndex && !this.tabObjects[index].disabled ? 'block' : 'none' } }, h("slot", { name: `${this.tabsId}_tab-${index}` }))))))));
    }
    static get is() { return "core-tabs-vertical"; }
    static get originalStyleUrls() {
        return {
            "$": ["core-tabs-vertical.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["core-tabs-vertical.css"]
        };
    }
    static get properties() {
        return {
            "activeTabIndex": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set a tab as active by providing the index (tab can not be disabled)."
                },
                "attribute": "active-tab-index",
                "reflect": false,
                "defaultValue": "0"
            },
            "width": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "'small' | 'medium' | 'large'",
                    "resolved": "\"large\" | \"medium\" | \"small\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Change the width of the tab list."
                },
                "attribute": "width",
                "reflect": false,
                "defaultValue": "'small'"
            }
        };
    }
    static get states() {
        return {
            "internalActiveTabIndex": {},
            "internalFocusedTabIndex": {},
            "tabRefs": {},
            "tabHeaderRefs": {},
            "tabObjects": {}
        };
    }
    static get events() {
        return [{
                "method": "coreTabChange",
                "name": "coreTabChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "element"; }
    static get watchers() {
        return [{
                "propName": "activeTabIndex",
                "methodName": "activeTabIndexChanged"
            }];
    }
    static get listeners() {
        return [{
                "name": "slotchange",
                "method": "onSlotChange",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "keydown",
                "method": "handleKeyDown",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=core-tabs-vertical.js.map
